/**
 * Terms of Service Translations Module (English)
 * 
 * @module terms
 * @language en
 */

export const terms = {
  pageTitle: "Terms of Service",
  heroSubtitle: "Please read these terms carefully before using our platform.",
  lastUpdated: "Last Updated: January 2026",
  effectiveDate: "Effective: January 1, 2026",

  intro: {
    title: "Introduction",
    welcome: "Welcome to the JCI Development Councils Youth Futures Observatory (the \"Platform\"). These Terms of Service (\"Terms\") govern your access to and use of our platform.",
    acceptance: "By accessing or using this Platform, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use the Platform.",
    modifications: "We may modify these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the modified Terms.",
  },

  eligibility: {
    title: "Eligibility",
    age: "You must be at least 18 years of age to use this Platform. By using the Platform, you represent and warrant that you meet this age requirement.",
    membership: "While the Platform is available to the general public, certain features may be restricted to JCI members or registered users.",
    accuracy: "You agree to provide accurate, current, and complete information during registration and to maintain and update such information.",
  },

  account: {
    title: "Account Responsibilities",
    security: "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
    notification: "You must notify us immediately of any unauthorized use of your account or any other breach of security.",
    prohibition: "You may not share your account credentials with others or allow others to access the Platform using your account.",
  },

  acceptableUse: {
    title: "Acceptable Use",
    description: "You agree to use the Platform only for lawful purposes and in accordance with these Terms.",
    prohibited: "You may not:",
    item1: "Use the Platform for any illegal or unauthorized purpose",
    item2: "Attempt to gain unauthorized access to any systems, networks, or data",
    item3: "Interfere with or disrupt the Platform or servers or networks connected to it",
    item4: "Use any automated means to access the Platform or collect data",
    item5: "Transmit any viruses, malware, or other harmful code",
    item6: "Impersonate any person or entity or misrepresent your affiliation",
    item7: "Collect or harvest personal information of other users without consent",
    item8: "Use the Platform to spam, phish, or engage in fraudulent activities",
  },

  intellectualProperty: {
    title: "Intellectual Property",
    jciTrademarks: "JCI, Junior Chamber International, the JCI logo, and all related trademarks, service marks, and logos are the property of Junior Chamber International. You may not use these marks without prior written permission.",
    dataLicense: "Data, visualizations, and content on this Platform are provided under the Creative Commons Attribution-NonCommercial-ShareAlike 4.0 International License (CC BY-NC-SA 4.0), unless otherwise indicated.",
    restrictions: "You may not use our intellectual property for commercial purposes without explicit written authorization.",
    platformCredits: "The Platform is developed and maintained by DashCity in partnership with the JCI Development Councils.",
  },

  dataPrivacy: {
    title: "Data & Privacy",
    reference: "Your use of the Platform is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information.",
    link: "Please review our Privacy Policy to understand our data practices.",
    gdpr: "We are committed to GDPR compliance and respect your data protection rights.",
  },

  thirdParty: {
    title: "Third-Party Services",
    description: "The Platform may contain links to third-party websites or services that are not owned or controlled by us.",
    disclaimer: "We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.",
    acknowledgment: "You acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by or in connection with your use of any third-party content, goods, or services.",
  },

  disclaimers: {
    title: "Disclaimers",
    asIs: "THE PLATFORM IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.",
    noWarranty: "We do not warrant that the Platform will be uninterrupted, secure, or error-free, or that any defects will be corrected.",
    accuracy: "While we strive to provide accurate data and information, we make no guarantees regarding the accuracy, completeness, or reliability of any content on the Platform.",
    research: "The data and insights provided are for informational purposes only and should not be the sole basis for decision-making.",
  },

  responsibilities: {
    title: "Your Responsibilities",
    description: "You are responsible for your use of the Platform and agree to use it in accordance with these Terms and all applicable laws. You understand that any misuse of the Platform may result in termination of your access.",
  },

  governingLaw: {
    title: "Governing Law",
    jurisdiction: "These Terms shall be governed by and construed in accordance with applicable international law principles and the laws of the jurisdiction where the primary Platform operations are conducted.",
    disputes: "Any disputes arising from these Terms or your use of the Platform shall be resolved through good-faith negotiation. If negotiation fails, disputes may be submitted to binding arbitration.",
    severability: "If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.",
  },

  termination: {
    title: "Termination",
    byUs: "We may terminate or suspend your access to the Platform at any time, with or without cause, and with or without notice.",
    byYou: "You may stop using the Platform at any time. If you have an account, you may delete it through the Settings page.",
    survival: "Provisions that by their nature should survive termination shall survive, including intellectual property provisions, disclaimers, and limitations of liability.",
  },

  contact: {
    title: "Contact Us",
    description: "If you have any questions about these Terms of Service, please contact us:",
    email: "Email: tmariano@jci.cc",
    response: "We aim to respond to all inquiries within 5 business days.",
  },
} as const;
